import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  deleteDoc,
  query, 
  orderBy, 
  where,
  writeBatch,
  Timestamp 
} from 'firebase/firestore';
import type { DocumentData } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import type { Questionnaire, Question, BilingualText, QuestionOption } from '@/types/questionnaire';

const COLLECTION_NAME = 'questionnaires';

// Helper function to ensure bilingual text structure
function ensureBilingualText(text: unknown): BilingualText {
  if (typeof text === 'string') {
    return { en: text, ar: text };
  }
  if (text && typeof text === 'object' && 'en' in text && 'ar' in text) {
    return text as BilingualText;
  }
  return { en: '', ar: '' };
}

// Helper function to ensure option has bilingual structure
function ensureBilingualOption(option: Record<string, unknown>): QuestionOption {
  return {
    id: (option.id as string) || crypto.randomUUID(),
    text: ensureBilingualText(option.text),
    score: (option.score as number) || 0
  };
}

// Helper function to convert Firestore data to Questionnaire
function convertFirestoreToQuestionnaire(id: string, data: DocumentData): Questionnaire {
  return {
    id,
    name: ensureBilingualText(data.name),
    description: data.description ? ensureBilingualText(data.description) : undefined,
    isDefault: data.isDefault || false,
    validityDays: typeof data.validityDays === 'number' ? data.validityDays : undefined,
    archived: data.archived === true,
    questions: (data.questions || []).map((q: DocumentData) => ({
      id: q.id,
      question: ensureBilingualText(q.question),
      options: (q.options || []).map(ensureBilingualOption),
      order: q.order || 0,
      category: q.category || 'Exercises'
    })),
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
    createdBy: data.createdBy || ''
  };
}

// Get all questionnaires
export async function getQuestionnaires(): Promise<Questionnaire[]> {
  try {
    const questionnairesRef = collection(db, COLLECTION_NAME);
    const q = query(questionnairesRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => convertFirestoreToQuestionnaire(doc.id, doc.data()));
  } catch (error) {
    console.error('Error fetching questionnaires:', error);
    throw error;
  }
}

// Get a specific questionnaire by ID
export async function getQuestionnaire(id: string): Promise<Questionnaire | null> {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return convertFirestoreToQuestionnaire(docSnap.id, docSnap.data());
    }
    return null;
  } catch (error) {
    console.error('Error fetching questionnaire:', error);
    throw error;
  }
}

// Get the default questionnaire
export async function getDefaultQuestionnaire(): Promise<Questionnaire | null> {
  try {
    const questionnairesRef = collection(db, COLLECTION_NAME);
    const q = query(questionnairesRef, where('isDefault', '==', true));
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return convertFirestoreToQuestionnaire(doc.id, doc.data());
    }
    return null;
  } catch (error) {
    console.error('Error fetching default questionnaire:', error);
    throw error;
  }
}

// Validate questionnaire has at least one question per category
// Validation lives in a pure module so it is unit-testable without Firebase.
export { validateQuestionnaire } from '@/lib/questionnaires/validate';
import { validateQuestionnaire } from '@/lib/questionnaires/validate';

// Save a questionnaire
export async function saveQuestionnaire(questionnaire: Omit<Questionnaire, 'id' | 'createdAt' | 'updatedAt'>, userId: string, allowEmpty = false): Promise<string> {
  try {
    // Validate questionnaire (skip empty validation for new templates)
    const validation = validateQuestionnaire(questionnaire as Questionnaire, allowEmpty);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.map(e => e.message).join(', ')}`);
    }

    const batch = writeBatch(db);
    
    // If this questionnaire is set as default, remove default from others
    if (questionnaire.isDefault) {
      const existingQuestionnaires = await getQuestionnaires();
      existingQuestionnaires.forEach(q => {
        if (q.isDefault) {
          const docRef = doc(db, COLLECTION_NAME, q.id);
          batch.update(docRef, { isDefault: false });
        }
      });
    }
    
    // Create new questionnaire - filter out undefined values
    const newDocRef = doc(collection(db, COLLECTION_NAME));
    const questionnaireData: Record<string, unknown> = {
      name: questionnaire.name,
      isDefault: questionnaire.isDefault || false,
      questions: questionnaire.questions || [],
      createdBy: userId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    // Only add description if it's defined and not undefined
    if (questionnaire.description !== undefined) {
      questionnaireData.description = questionnaire.description;
    }
    
    batch.set(newDocRef, questionnaireData);
    await batch.commit();
    
    return newDocRef.id;
  } catch (error) {
    console.error('Error saving questionnaire:', error);
    throw error;
  }
}

// Update an existing questionnaire
export async function updateQuestionnaire(id: string, updates: Partial<Questionnaire>): Promise<void> {
  try {
    // If updating questions, validate first (but allow empty questionnaires)
    if (updates.questions) {
      const currentQuestionnaire = await getQuestionnaire(id);
      if (currentQuestionnaire) {
        const updatedQuestionnaire = { ...currentQuestionnaire, ...updates };
        const validation = validateQuestionnaire(updatedQuestionnaire); // Use flexible validation
        if (!validation.isValid) {
          throw new Error(`Validation failed: ${validation.errors.map(e => e.message).join(', ')}`);
        }
      }
    }

    const batch = writeBatch(db);
    
    // If setting as default, remove default from others
    if (updates.isDefault === true) {
      const existingQuestionnaires = await getQuestionnaires();
      existingQuestionnaires.forEach(q => {
        if (q.isDefault && q.id !== id) {
          const docRef = doc(db, COLLECTION_NAME, q.id);
          batch.update(docRef, { isDefault: false });
        }
      });
    }
    
    // Update the questionnaire - filter out undefined values
    const docRef = doc(db, COLLECTION_NAME, id);
    const updateData: DocumentData = {
      updatedAt: Timestamp.now()
    };
    
    // Only add fields that are not undefined
    Object.keys(updates).forEach(key => {
      const value = (updates as Record<string, unknown>)[key];
      if (value !== undefined) {
        updateData[key] = value;
      }
    });
    
    batch.update(docRef, updateData);
    
    await batch.commit();
  } catch (error) {
    console.error('Error updating questionnaire:', error);
    throw error;
  }
}

// Delete a questionnaire
export async function deleteQuestionnaire(id: string): Promise<void> {
  try {
    const questionnaire = await getQuestionnaire(id);
    if (questionnaire?.isDefault) {
      throw new Error('Cannot delete the default questionnaire');
    }
    
    const docRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting questionnaire:', error);
    throw error;
  }
}

// Set a questionnaire as default
export async function setDefaultQuestionnaire(id: string): Promise<void> {
  try {
    const batch = writeBatch(db);
    
    // Remove default from all questionnaires
    const existingQuestionnaires = await getQuestionnaires();
    existingQuestionnaires.forEach(q => {
      const docRef = doc(db, COLLECTION_NAME, q.id);
      batch.update(docRef, { isDefault: false });
    });
    
    // Set the specified questionnaire as default
    const docRef = doc(db, COLLECTION_NAME, id);
    batch.update(docRef, { isDefault: true });
    
    await batch.commit();
  } catch (error) {
    console.error('Error setting default questionnaire:', error);
    throw error;
  }
}

// Legacy function to get questions from default questionnaire (for backward compatibility)
export async function getScaleQuestions(): Promise<Question[]> {
  try {
    const defaultQuestionnaire = await getDefaultQuestionnaire();
    return defaultQuestionnaire?.questions || [];
  } catch (error) {
    console.error('Error fetching scale questions:', error);
    throw error;
  }
}
